/**
 * @file ProductsTable.tsx
 *
 * List-view table for products: a sortable name/price header, status and stock
 * indicators, category summary, and a per-row actions trigger. Columns collapse
 * on smaller viewports via Gravity column-hiding classes plus a stylesheet
 * breakpoint. The pager is rendered beneath via the shared {@link Pagination}.
 */

import { Icon, Indicator } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import type { ShopProduct } from '../api/shop-types';
import { ImageOrDefault } from '../components/ImageOrDefault';
import { Pagination } from '../components/Pagination';
import {
  STATUS_FILTER_LABELS,
  STATUS_INDICATOR,
  STOCK_INDICATOR,
  STOCK_LABELS,
  productPriceDisplay,
  productTypeBadge,
  type SortColumn,
  type SortOrder,
} from './product-list';
import styles from './ProductsView.module.css';

const TABLE_CLASS = [
  'gv-col-1-shrink',
  'gv-col-8-shrink',
  'gv-col-8-text-right',
  'gv-max-mob-col-1-hidden',
  'gv-max-mob-col-3-hidden',
  'gv-max-tab-col-4-hidden',
  'gv-max-tab-col-5-hidden',
  'gv-max-tab-col-6-hidden',
  'gv-max-tab-col-7-hidden',
].join(' ');

interface ProductsTableProps {
  products: ShopProduct[];
  currencySymbol: string;
  sortBy: SortColumn | null;
  sortOrder: SortOrder;
  onSort: (column: SortColumn) => void;
  menuProductId: string | null;
  onMore: (product: ShopProduct, triggerEl: HTMLButtonElement) => void;
  selectedId: string | null;
  onSelect: (product: ShopProduct) => void;
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
}

function ariaSort(
  active: boolean,
  order: SortOrder,
): 'ascending' | 'descending' | 'none' {
  if (!active) {
    return 'none';
  }
  return order === 'asc' ? 'ascending' : 'descending';
}

/**
 * Renders the products list table and its pager.
 *
 * @param props - Products, currency, sort state, selection, and menu handlers.
 *
 * @returns The table element.
 */
export function ProductsTable({
  products,
  currencySymbol,
  sortBy,
  sortOrder,
  onSort,
  menuProductId,
  onMore,
  selectedId,
  onSelect,
  page,
  totalPages,
  onPage,
}: ProductsTableProps) {
  const intl = useIntl();

  return (
    <div className="gv-data-table">
      <table
        className={`${TABLE_CLASS} ${styles.productListTable}`}
        data-testid="products-table"
      >
        <thead>
          <tr>
            <th aria-hidden="true" />
            <th scope="col" aria-sort={ariaSort(sortBy === 'title', sortOrder)}>
              <button
                className="gv-th-sort"
                type="button"
                data-testid="products-sort-name"
                onClick={() => onSort('title')}
              >
                <span>
                  <FormattedMessage {...adminMessages.productsFieldName} />
                </span>
                <div
                  className={`gv-sort ${sortBy === 'title' ? (sortOrder === 'asc' ? 'gv-asc' : 'gv-desc') : ''}`}
                >
                  <Indicator name="sort_asc" />
                  <Indicator name="sort_desc" />
                </div>
              </button>
            </th>
            <th scope="col" aria-sort={ariaSort(sortBy === 'status', sortOrder)}>
              <button
                className="gv-th-sort"
                type="button"
                data-testid="products-sort-status"
                onClick={() => onSort('status')}
              >
                <span>
                  <FormattedMessage {...adminMessages.productsFieldStatus} />
                </span>
                <div
                  className={`gv-sort ${sortBy === 'status' ? (sortOrder === 'asc' ? 'gv-asc' : 'gv-desc') : ''}`}
                >
                  <Indicator name="sort_asc" />
                  <Indicator name="sort_desc" />
                </div>
              </button>
            </th>
            <th scope="col">
              <FormattedMessage {...adminMessages.productsFieldType} />
            </th>
            <th scope="col">
              <FormattedMessage {...adminMessages.productsFieldCategory} />
            </th>
            <th scope="col" aria-sort={ariaSort(sortBy === 'stock', sortOrder)}>
              <button
                className="gv-th-sort"
                type="button"
                data-testid="products-sort-stock"
                onClick={() => onSort('stock')}
              >
                <span>
                  <FormattedMessage {...adminMessages.productsFieldStock} />
                </span>
                <div
                  className={`gv-sort ${sortBy === 'stock' ? (sortOrder === 'asc' ? 'gv-asc' : 'gv-desc') : ''}`}
                >
                  <Indicator name="sort_asc" />
                  <Indicator name="sort_desc" />
                </div>
              </button>
            </th>
            <th scope="col" aria-sort={ariaSort(sortBy === 'price', sortOrder)}>
              <button
                className="gv-th-sort"
                type="button"
                data-testid="products-sort-price"
                onClick={() => onSort('price')}
              >
                <span>
                  <FormattedMessage {...adminMessages.productsFieldPrice} />
                </span>
                <div
                  className={`gv-sort ${sortBy === 'price' ? (sortOrder === 'asc' ? 'gv-asc' : 'gv-desc') : ''}`}
                >
                  <Indicator name="sort_asc" />
                  <Indicator name="sort_desc" />
                </div>
              </button>
            </th>
            <th scope="col" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {products.map((product) => {
            const price = productPriceDisplay(product, currencySymbol).label;
            const statusIndicator =
              STATUS_INDICATOR[product.status] ?? 'gv-state-attention';
            const statusLabel = STATUS_FILTER_LABELS[product.status];
            const stockIndicator =
              STOCK_INDICATOR[product.stock_status] ?? 'gv-state-attention';
            const stockLabel = STOCK_LABELS[product.stock_status]
              ? intl.formatMessage(STOCK_LABELS[product.stock_status])
              : product.stock_status;
            const typeBadge = productTypeBadge(product);
            // For a variant product show the sellable tracked total; for a simple product the
            // in-stock quantity (as before). `null` when nothing tracked.
            const stockCount =
              product.stock_summary && product.stock_summary.trackedCount > 0
                ? product.stock_summary.trackedQuantitySum
                : product.stock_status === 'instock'
                  ? product.stock_quantity
                  : null;
            const firstCategory = product.categories[0]?.name;
            const extraCategories = product.categories.length - 1;
            const isSelected = selectedId === product.id;

            return (
              <tr
                key={product.id}
                style={{ cursor: 'pointer' }}
                data-testid="product-row"
                data-id={product.id}
                data-selected={isSelected || undefined}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => onSelect(product)}
              >
                <td>
                  <ImageOrDefault
                    src={product.images[0]?.src}
                    alt={product.name}
                    className="gv-thumbnail-image"
                  />
                </td>
                <td className="gv-col-truncate">
                  <div className="gv-flex gv-flex-col">
                    <span className="gv-text-sm gv-text-truncate">{product.name}</span>
                    {product.sku && (
                      <span className={`gv-caption-sm gv-text-truncate ${styles.productSku}`}>
                        {intl.formatMessage(adminMessages.productsFieldSku)}: {product.sku}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="gv-text-indicator">
                    <div className={`gv-indicator ${statusIndicator}`} />
                    <span className={styles.capitalize}>
                      {statusLabel ? intl.formatMessage(statusLabel) : product.status}
                    </span>
                  </div>
                </td>
                <td>
                  <span className="gv-badge gv-badge-generic">
                    {intl.formatMessage(typeBadge.message, typeBadge.values)}
                  </span>
                </td>
                <td>
                  {firstCategory ? (
                    <div className="gv-flex gv-flex-col">
                      <span className="gv-text-sm">{firstCategory}</span>
                      {extraCategories > 0 && (
                        <span className={`gv-caption-sm ${styles.subCategory}`}>
                          <FormattedMessage
                            {...adminMessages.productsMoreCategories}
                            values={{ count: extraCategories }}
                          />
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="gv-text-sm">—</span>
                  )}
                </td>
                <td>
                  <div className="gv-text-indicator">
                    <div className={`gv-indicator ${stockIndicator}`} />
                    <span>
                      {stockLabel}
                      {stockCount != null && (
                        <span className="gv-text-muted"> ({stockCount})</span>
                      )}
                    </span>
                  </div>
                </td>
                <td>{price ? <span className="gv-text-sm">{price}</span> : '—'}</td>
                <td>
                  <button
                    className="gv-button gv-button-icon"
                    type="button"
                    data-testid="product-row-actions"
                    aria-label={intl.formatMessage(adminMessages.productsActions)}
                    aria-expanded={menuProductId === product.id}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onMore(product, e.currentTarget);
                    }}
                  >
                    <Icon name="more_horiz" size="small" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        page={page}
        totalPages={totalPages}
        surfaceClassName={products.length % 2 === 0 ? 'gv-surface-dim' : 'gv-surface-bright'}
        onPage={onPage}
        navLabel={intl.formatMessage(adminMessages.productsTitle)}
        testIdPrefix="products-pagination"
      />
    </div>
  );
}
